import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { TableBody, TableRow, TableCell, Table, TableHead, IconButton } from '@mui/material';
import { observer } from 'mobx-react';
import { Label } from '@components/Label';
import { TableHeaderCell } from '@components/index';
import { fonts } from '@theme/fontsCustomer';
import { ChevronIcon } from '@icons/ChevronIcon';
import type { UserLicenseListFilter } from '@repositories/subscriptionUserRepository/subscriptionUser.types';
import { useSubscriptionUserLicenses } from '@queryHooks/useSubscriptionUser';
import { useGetTenantMember } from '@queryHooks/useTenant';
import { getSortOrder } from '@utils/index';
import { SubscriptionRow } from './SubscriptionRow';

export const LicenseRetrieve = observer(() => {
  const { t } = useTranslation();
  const params = useParams();
  const { tenantId, userId } = params;
  const { data: user, isSuccess } = useGetTenantMember(tenantId as string, userId as string);
  const [autoLicensesFilter, setAutoLicensesFilter] = useState<UserLicenseListFilter>({ sort: [], loginId: '' });
  const [manualLicensesFilter, setManualLicensesFilter] = useState<UserLicenseListFilter>({ sort: [], loginId: '' });
  const { data: userLicenses } = useSubscriptionUserLicenses(
    { ...autoLicensesFilter, loginId: user?.username as string },
    { enabled: isSuccess },
  );
  const autoLicenses = userLicenses?.content.filter(license => license.canRemoveUser);
  const manualLicenses = userLicenses?.content.filter(license => !license.canRemoveUser);
  const navigate = useNavigate();

  const handleLicensesFilterUpdate = (
    filterStateSetter: React.Dispatch<React.SetStateAction<UserLicenseListFilter>>,
    orderBy: string,
    order: 'asc' | 'desc' | undefined,
  ) => {
    filterStateSetter((prev: UserLicenseListFilter) => ({
      ...prev,
      sort: order ? [`${orderBy}.${order}`] : undefined,
      loginId: user?.username as string,
    }));
  };

  const handleAutoLicensesFilterUpdateFilter = {
    updateSort: (orderBy: string, order: 'asc' | 'desc' | undefined) =>
      handleLicensesFilterUpdate(setAutoLicensesFilter, orderBy, order),
  };

  const handleManualLicensesUpdateFilter = {
    updateSort: (orderBy: string, order: 'asc' | 'desc' | undefined) =>
      handleLicensesFilterUpdate(setManualLicensesFilter, orderBy, order),
  };

  return (
    <Container>
      <Header>
        <IconButton onClick={() => navigate(-1)}>
          <ChevronIcon rotateNum={270} />
        </IconButton>
        <strong>{t('License_Retrieve.Title')}</strong>
      </Header>
      <Body>
        <UserInfoWrapper>
          <UserInfoTitle>{t('License_Retrieve.Member_Info')}</UserInfoTitle>
          <Divider />
          <UserInfoDetail>
            <TableBody>
              <StyledTableRow>
                <TableCell>
                  <UserInfoDetailEl>
                    <UserInfoDetailElName>{t('License_Retrieve.Category')}</UserInfoDetailElName>
                    <UserInfoDetailElValue>
                      <Label variant="solid" color="red">
                        {t('License_Retrieve.Withdrawn_Member')}
                      </Label>
                    </UserInfoDetailElValue>
                  </UserInfoDetailEl>
                </TableCell>
                <TableCell>
                  <UserInfoDetailEl>
                    <UserInfoDetailElName>{t('License_Retrieve.Withdrawal_Date')}</UserInfoDetailElName>
                    <UserInfoDetailElValue>{user?.deletedDate || '-'}</UserInfoDetailElValue>
                  </UserInfoDetailEl>
                </TableCell>
                <TableCell>
                  <UserInfoDetailEl />
                </TableCell>
                <TableCell>
                  <UserInfoDetailEl />
                </TableCell>
              </StyledTableRow>
              <StyledTableRow>
                <TableCell>
                  <UserInfoDetailEl>
                    <UserInfoDetailElName>{t('License_Retrieve.Name')}</UserInfoDetailElName>
                    <UserInfoDetailElValue>{user?.name}</UserInfoDetailElValue>
                  </UserInfoDetailEl>
                </TableCell>
                <TableCell>
                  <UserInfoDetailEl>
                    <UserInfoDetailElName>{t('License_Retrieve.Account')}</UserInfoDetailElName>
                    <UserInfoDetailElValue>{user?.email}</UserInfoDetailElValue>
                  </UserInfoDetailEl>
                </TableCell>
                <TableCell>
                  <UserInfoDetailEl>
                    <UserInfoDetailElName>{t('License_Retrieve.Group')}</UserInfoDetailElName>
                    <UserInfoDetailElValue>
                      {user?.usergroups.map(group => group).length ? user?.usergroups.map(group => group) : '-'}
                    </UserInfoDetailElValue>
                  </UserInfoDetailEl>
                </TableCell>
                <TableCell>
                  <UserInfoDetailEl>
                    <UserInfoDetailElName>{t('License_Retrieve.Contact')}</UserInfoDetailElName>
                    <UserInfoDetailElValue>{user?.phone}</UserInfoDetailElValue>
                  </UserInfoDetailEl>
                </TableCell>
              </StyledTableRow>
            </TableBody>
          </UserInfoDetail>
          <Divider className="bottom" />
        </UserInfoWrapper>
        <UseSubscriptionListWrapper>
          <UseSubscriptionListTitle>{t('License_Retrieve.Usage_Items')}</UseSubscriptionListTitle>
          <LicenseRetrieveList>
            {autoLicenses?.length === 0 ? null : (
              <div>
                <SubscriptionType>{t('License_Retrieve.Auto_Retrieve')}</SubscriptionType>
                <SubscriptionTypeDescription>{t('License_Retrieve.Auto_Description')}</SubscriptionTypeDescription>
                <TableWrapper>
                  <TableHead>
                    <StyledTableRow>
                      <TableCell width={342}>{t('License_Retrieve.Subscription_Name')}</TableCell>
                      <TableCell width={240}>{t('License_Retrieve.Product_Name')}</TableCell>
                      <TableCell width={240}>{t('License_Retrieve.License_Code')}</TableCell>
                      <TableCell width={180}>{t('License_Retrieve.License_Status')}</TableCell>
                      <TableCell width={120}>{t('License_Retrieve.User_Status')}</TableCell>
                      <TableCell width={160}>{t('License_Retrieve.Last_Used_Date')}</TableCell>
                      <TableHeaderCell
                        width={120}
                        orderBy="billableStatus"
                        order={getSortOrder(autoLicensesFilter.sort, 'billableStatus')}
                        onSortChange={handleAutoLicensesFilterUpdateFilter.updateSort}
                      >
                        {t('License_Retrieve.Billing_Status')}
                      </TableHeaderCell>
                      <TableCell width={150}>{t('License_Retrieve.Management')}</TableCell>
                    </StyledTableRow>
                  </TableHead>
                  <TableBody>
                    {autoLicenses?.map(license => {
                      return <SubscriptionRow key={license.subscription.id} license={license} type="auto" />;
                    })}
                  </TableBody>
                </TableWrapper>
              </div>
            )}
            {manualLicenses?.length === 0 ? null : (
              <div>
                <SubscriptionType>{t('License_Retrieve.Manual_Retrieve')}</SubscriptionType>
                <SubscriptionTypeDescription>{t('License_Retrieve.Manual_Description')}</SubscriptionTypeDescription>
                <TableWrapper>
                  <TableHead>
                    <StyledTableRow>
                      <TableCell width={342}>{t('License_Retrieve.Subscription_Name')}</TableCell>
                      <TableCell width={240}>{t('License_Retrieve.Product_Name')}</TableCell>
                      <TableCell width={240}>{t('License_Retrieve.License_Code')}</TableCell>
                      <TableCell width={180}>{t('License_Retrieve.License_Status')}</TableCell>
                      <TableCell width={120}>{t('License_Retrieve.User_Status')}</TableCell>
                      <TableCell width={160}>{t('License_Retrieve.Last_Used_Date')}</TableCell>
                      <TableHeaderCell
                        width={120}
                        orderBy="billableStatus"
                        order={getSortOrder(manualLicensesFilter.sort, 'billableStatus')}
                        onSortChange={handleManualLicensesUpdateFilter.updateSort}
                      >
                        {t('License_Retrieve.Billing_Status')}
                      </TableHeaderCell>
                      <TableCell width={150}>{t('License_Retrieve.Management')}</TableCell>
                    </StyledTableRow>
                  </TableHead>
                  <TableBody>
                    {manualLicenses?.map(license => {
                      return <SubscriptionRow key={license.subscription.id} license={license} type="manual" />;
                    })}
                  </TableBody>
                </TableWrapper>
              </div>
            )}
          </LicenseRetrieveList>
        </UseSubscriptionListWrapper>
      </Body>
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100% - 88px);
  height: calc(100vh - var(--manager-gnb-height));
  padding: 28px 56px 28px 32px;
  overflow-y: auto;
`;
const TableWrapper = styled(Table)`
  border-radius: 5px;
  box-shadow: 0 0 0 1px ${({ theme }) => theme.colors['border-gray-lighter']};
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${fonts.Headline5};
  color: ${props => props.theme.colors['text-gray-main']};
  margin-bottom: 40px;
  gap: 4px;
`;

const Body = styled.div`
  margin: 0 0 0 24px;
  display: flex;
  flex-direction: column;
  gap: 72px;
`;

const UserInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserInfoTitle = styled.strong`
  ${fonts.Headline7};
  color: ${props => props.theme.colors['text-gray-main']};
`;

const Divider = styled.hr`
  margin: 16px 0px 0px 0px;
  width: 100%;
  border: 0;
  height: 1px;
  background: ${props => props.theme.colors['border-gray-light']};
  &.bottom {
    margin: 0;
  }
`;

const UserInfoDetail = styled(Table)`
  & > tbody td {
    height: 72px;
  }
`;

const UserInfoDetailEl = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: inherit;
  justify-content: center;
  padding: 0px 4px;
`;

const UserInfoDetailElName = styled.span`
  ${fonts.Headline9};
  color: ${props => props.theme.colors['text-gray-sub-dark']};
`;

const UserInfoDetailElValue = styled.span`
  ${fonts.Headline8};
  color: ${props => props.theme.colors['text-gray-main']};
`;

const UseSubscriptionListWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const UseSubscriptionListTitle = styled.span`
  ${fonts.Headline7};
  color: ${props => props.theme.colors['text-gray-main']};
  margin-bottom: 16px;
`;

const SubscriptionType = styled.strong`
  ${fonts.Headline8};
  color: ${props => props.theme.colors['text-gray-main']};
  margin-bottom: 2px;
`;

const SubscriptionTypeDescription = styled.p`
  ${fonts.Body2};
  color: ${props => props.theme.colors['text-gray-sub-dark']};
  margin-bottom: 12px;
`;

const LicenseRetrieveList = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: 48px;
`;

const StyledTableRow = styled(TableRow)`
  &:hover {
    background-color: ${({ theme }) => theme.colors['bg-white']};
  }
`;
